import React, { useState, useEffect } from 'react';
import './PlanejadorCliente.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br')
  : (process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000');

// Helper to get auth header
const getAuthHeader = () => {
  return {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Format date
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

// Calculate progress percentage
const calculateProgress = (item) => {
  if (!item.totalValue || item.totalValue === 0) return 0;
  const percentage = (item.totalConsolidado / item.totalValue) * 100;
  // Don't cap at 100% - show actual percentage
  return Math.round(percentage);
};

// Calculate activities progress
const calculateActivitiesProgress = (item) => {
  let totalActivities = 0;
  let completedActivities = 0;
  
  // Count selected services
  if (item.selectedServicos && item.selectedServicos.length > 0) {
    totalActivities += item.selectedServicos.length;
    completedActivities += item.selectedServicos.filter(s => s.completed).length;
  }
  
  // Count custom activities
  if (item.customActivities && item.customActivities.length > 0) {
    totalActivities += item.customActivities.length;
    completedActivities += item.customActivities.filter(a => a.completed).length;
  }
  
  // Count reimbursements
  if (item.reembolsos && item.reembolsos.length > 0) {
    totalActivities += item.reembolsos.length;
    completedActivities += item.reembolsos.filter(r => r.completed).length;
  }
  
  return {
    total: totalActivities,
    completed: completedActivities,
    percentage: totalActivities > 0 ? Math.round((completedActivities / totalActivities) * 100) : 0
  };
};

// Status labels in Portuguese
const statusLabels = {
  todo: 'A Fazer',
  inProgress: 'Em Progresso',
  done: 'Concluído'
};

// Priority labels in Portuguese
const priorityLabels = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta'
};

const PlanejadorCliente = () => {
  const [workItems, setWorkItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [authorizing, setAuthorizing] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState({
    editRequests: [],
    highValueApprovals: []
  });
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Fetch work items
  const fetchWorkItems = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await fetch(`${API_BASE_URL}/api/clientes/work-items`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setWorkItems(data.data || []);
      setSummary(data.summary || null);
      setError(null);
      
      // Update last refresh time
      if (!showLoading) {
        setLastRefresh(new Date());
      }
    } catch (err) {
      setError('Erro ao carregar os pedidos e serviços. Por favor, tente novamente.');
      console.error('Erro ao buscar work items:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };
  
  // Fetch pending approvals
  const fetchPendingApprovals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clientes/pending-approvals`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingApprovals(data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar aprovações pendentes:', error);
    }
  };

  useEffect(() => {
    // Initial load
    fetchWorkItems();
    fetchPendingApprovals();
    
    // Auto-refresh setup
    const interval = setInterval(() => {
      // Check if page is visible
      if (document.hidden) {
        return; // Don't refresh if tab is not visible
      }
      
      // Check if modals are open by reading the DOM
      const modalElements = document.querySelectorAll('.modal-overlay');
      const hasOpenModals = modalElements.length > 0;
      
      // Only refresh if no modals are open
      if (!hasOpenModals) {
        // Silent refresh without loading spinner
        fetchWorkItems(false);
        fetchPendingApprovals();
      }
    }, 30000); // 30 seconds
    
    // Refresh when tab becomes visible after being hidden
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const modalElements = document.querySelectorAll('.modal-overlay');
        const hasOpenModals = modalElements.length > 0;
        
        if (!hasOpenModals) {
          fetchWorkItems(false);
          fetchPendingApprovals();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - only run once

  // Filter work items
  const filteredWorkItems = workItems.filter(item => {
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesSearch = !searchTerm || 
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  // Group work items by status
  const groupedItems = {
    todo: filteredWorkItems.filter(item => item.status === 'todo'),
    inProgress: filteredWorkItems.filter(item => item.status === 'inProgress'),
    done: filteredWorkItems.filter(item => item.status === 'done')
  };

  // Handle card click
  const handleCardClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };
  
  // Handle authorization
  const handleAuthorize = async (itemId) => {
    if (!window.confirm('Deseja autorizar este serviço? Após autorizado, o trabalho poderá ser iniciado.')) {
      return;
    }
    
    setAuthorizing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/clientes/work-items/${itemId}/authorize`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao autorizar');
      }
      
      const result = await response.json();
      
      // Update the work item in the list
      setWorkItems(prev => prev.map(item => 
        item._id === itemId ? result.data : item
      ));
      
      // Update selected item if it's the same
      if (selectedItem && selectedItem._id === itemId) {
        setSelectedItem(result.data);
      }
      
      alert('Serviço autorizado com sucesso!');
    } catch (err) {
      alert('Erro ao autorizar: ' + err.message);
      console.error('Erro ao autorizar:', err);
    } finally {
      setAuthorizing(false);
    }
  };
  
  // Handle process edit request
  const handleProcessEditRequest = async (editRequestId, approvedItems, clientResponse) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clientes/edit-requests/${editRequestId}/process`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ approvedItems, clientResponse })
      });
      
      if (response.ok) {
        alert('Solicitação processada com sucesso!');
        fetchPendingApprovals();
        fetchWorkItems();
        setShowApprovalModal(false);
      } else {
        const error = await response.json();
        alert('Erro ao processar solicitação: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao processar solicitação: ' + error.message);
    }
  };
  
  // Handle process high value approval
  const handleProcessHighValueApproval = async (approvalId, approved) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/clientes/approvals/${approvalId}/process`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ approved, clientResponse: '' })
      });
      
      if (response.ok) {
        alert(approved ? 'Item aprovado com sucesso!' : 'Item rejeitado.');
        fetchPendingApprovals();
        fetchWorkItems();
      } else {
        const error = await response.json();
        alert('Erro ao processar aprovação: ' + error.message);
      }
    } catch (error) {
      alert('Erro ao processar aprovação: ' + error.message);
    }
  };

  // State for edit request approval modal
  const [approvedItems, setApprovedItems] = useState([]);
  const [clientResponse, setClientResponse] = useState('');
  
  // Handle opening the approval modal
  const handleOpenApprovalModal = (approval) => {
    setSelectedApproval(approval);
    if (approval.type === 'edit') {
      setApprovedItems(approval.data.items.map(() => true));
      setClientResponse('');
    }
    setShowApprovalModal(true);
  };
  
  // Edit Request Approval Modal
  const EditRequestApprovalModal = () => {
    if (!selectedApproval || selectedApproval.type !== 'edit') return null;
    
    const handleToggleItem = (index) => {
      setApprovedItems(prev => {
        const newApprovedItems = [...prev];
        newApprovedItems[index] = !newApprovedItems[index];
        return newApprovedItems;
      });
    };
    
    const handleSubmit = () => {
      handleProcessEditRequest(selectedApproval.data._id, approvedItems, clientResponse);
    };
    
    const handleClose = () => {
      setShowApprovalModal(false);
      // Reset states when closing
      setApprovedItems([]);
      setClientResponse('');
      setSelectedApproval(null);
    };
    
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Aprovar Solicitação de Edição</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="approval-info">
              <p><strong>Serviço:</strong> {selectedApproval.data.workItemId.title}</p>
              <p><strong>Solicitado por:</strong> {selectedApproval.data.requestedBy.nomeCompleto}</p>
              <p><strong>Data:</strong> {formatDate(selectedApproval.data.requestedAt)}</p>
            </div>
            
            <h3>Itens para Aprovação</h3>
            <table className="approval-items-table">
              <thead>
                <tr>
                  <th>Aprovar</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {selectedApproval.data.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={approvedItems[index]}
                        onChange={() => handleToggleItem(index)}
                      />
                    </td>
                    <td>{item.type === 'activity' ? 'Atividade' : 'Reembolso'}</td>
                    <td>{item.nome || item.descricao}</td>
                    <td>{formatCurrency(item.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3"><strong>Total</strong></td>
                  <td><strong>{formatCurrency(selectedApproval.data.totalValue)}</strong></td>
                </tr>
              </tfoot>
            </table>
            
            <div className="approval-response">
              <label>Observações (opcional):</label>
              <textarea
                value={clientResponse}
                onChange={(e) => setClientResponse(e.target.value)}
                placeholder="Adicione comentários sobre a aprovação..."
                rows="3"
              />
            </div>
            
            <div className="details-actions">
              <button className="btn-secondary" onClick={handleClose}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleSubmit}>
                Processar Aprovação
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Work Item Card Component
  const WorkItemCard = ({ item }) => {
    const progress = calculateProgress(item);
    const activities = calculateActivitiesProgress(item);
    const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'done';
    
    return (
      <div className={`planner-card ${isOverdue ? 'overdue' : ''} ${!item.authorized ? 'unauthorized' : ''}`} onClick={() => handleCardClick(item)}>
        <div className="card-header">
          <h4>{item.title}</h4>
          <div className="card-badges">
            {!item.authorized && (
              <span className="auth-badge pending">
                🔒 Pendência de Autorização
              </span>
            )}
            <span className={`priority-badge priority-${item.priority}`}>
              {priorityLabels[item.priority]}
            </span>
          </div>
        </div>
        
        {item.description && (
          <p className="card-description">{item.description}</p>
        )}
        
        <div className="card-info">
          <div className="info-row">
            <span className="info-label">Prazo:</span>
            <span className={`info-value ${isOverdue ? 'text-danger' : ''}`}>
              {formatDate(item.dueDate)}
            </span>
          </div>
          
          <div className="info-row">
            <span className="info-label">Responsável:</span>
            <span className="info-value">
              {item.assignedTo?.nomeCompleto || 'Não atribuído'}
            </span>
          </div>
        </div>
        
        <div className="card-financial">
          <div className="financial-row">
            <span>Valor Previsto:</span>
            <span>{formatCurrency(item.totalValue)}</span>
          </div>
          <div className="financial-row">
            <span>Valor Realizado:</span>
            <span className="text-success">{formatCurrency(item.totalConsolidado)}</span>
          </div>
        </div>
        
        <div className="progress-section">
          <div className="progress-item">
            <span className="progress-label">Financeiro:</span>
            <div className="progress-container inline">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${Math.min(progress, 100)}%`,
                    backgroundColor: progress > 100 ? '#dc3545' : undefined
                  }}
                />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>
          </div>
          
          <div className="progress-item">
            <span className="progress-label">Atividades:</span>
            <span className="activities-text">
              {activities.total === 0 
                ? 'Sem atividades' 
                : `${activities.completed}/${activities.total} (${activities.percentage}%)`
              }
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Work Item Modal Component
  const WorkItemModal = () => {
    if (!selectedItem) return null;
    
    const progress = calculateProgress(selectedItem);
    const isOverdue = selectedItem.dueDate && new Date(selectedItem.dueDate) < new Date() && selectedItem.status !== 'done';
    
    return (
      <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{selectedItem.title}</h2>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
          </div>
          
          {!selectedItem.authorized && (
            <div className="authorization-banner">
              <div className="auth-banner-content">
                <span className="auth-icon">🔒</span>
                <div>
                  <h4>Autorização Pendente</h4>
                  <p>Este serviço precisa ser autorizado para que o trabalho possa ser iniciado.</p>
                </div>
              </div>
              <button 
                className="btn-authorize"
                onClick={() => handleAuthorize(selectedItem._id)}
                disabled={authorizing}
              >
                {authorizing ? 'Autorizando...' : 'Autorizar Serviço'}
              </button>
            </div>
          )}
          
          <div className="modal-body">
            <div className="modal-section">
              <h3>Informações Gerais</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Status:</label>
                  <span className={`status-badge status-${selectedItem.status}`}>
                    {statusLabels[selectedItem.status]}
                  </span>
                </div>
                <div className="info-item">
                  <label>Prioridade:</label>
                  <span className={`priority-badge priority-${selectedItem.priority}`}>
                    {priorityLabels[selectedItem.priority]}
                  </span>
                </div>
                <div className="info-item">
                  <label>Status de Autorização:</label>
                  <span className={`auth-status ${selectedItem.authorized ? 'authorized' : 'pending'}`}>
                    {selectedItem.authorized ? '✓ Autorizado' : '⚠ Pendente'}
                  </span>
                </div>
                {selectedItem.authorized && selectedItem.authorizedAt && (
                  <div className="info-item">
                    <label>Autorizado em:</label>
                    <span>{formatDate(selectedItem.authorizedAt)}</span>
                  </div>
                )}
                <div className="info-item">
                  <label>Prazo:</label>
                  <span className={isOverdue ? 'text-danger' : ''}>
                    {formatDate(selectedItem.dueDate)}
                  </span>
                </div>
                <div className="info-item">
                  <label>Criado em:</label>
                  <span>{formatDate(selectedItem.createdAt)}</span>
                </div>
                <div className="info-item">
                  <label>Responsável:</label>
                  <span>{selectedItem.assignedTo?.nomeCompleto || 'Não atribuído'}</span>
                </div>
                <div className="info-item">
                  <label>Criado por:</label>
                  <span>{selectedItem.createdBy?.nomeCompleto || '-'}</span>
                </div>
              </div>
              
              {selectedItem.description && (
                <>
                  <h4>Descrição</h4>
                  <p className="description-text">{selectedItem.description}</p>
                </>
              )}
            </div>
            
            <div className="modal-section">
              <h3>Informações Financeiras</h3>
              <div className="financial-summary">
                <div className="financial-card">
                  <label>Valor Total Previsto</label>
                  <span className="financial-value">{formatCurrency(selectedItem.totalValue)}</span>
                </div>
                <div className="financial-card">
                  <label>Valor Total Realizado</label>
                  <span className="financial-value text-success">
                    {formatCurrency(selectedItem.totalConsolidado)}
                  </span>
                </div>
                <div className="financial-card">
                  <label>Progresso</label>
                  <div className="progress-container">
                    <div className="progress-bar large">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${Math.min(progress, 100)}%`,
                          backgroundColor: progress > 100 ? '#dc3545' : undefined
                        }}
                      />
                    </div>
                    <span className="progress-text">{progress}%</span>
                  </div>
                </div>
                <div className="financial-card">
                  <label>Atividades</label>
                  <div className="activities-progress">
                    <span className="activities-count">
                      {(() => {
                        const activities = calculateActivitiesProgress(selectedItem);
                        return `${activities.completed} de ${activities.total}`;
                      })()}
                    </span>
                    <span className="activities-percentage">
                      {calculateActivitiesProgress(selectedItem).percentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-section">
              <h3>Atividades e Serviços</h3>
              
              {selectedItem.selectedServicos?.length > 0 && (
                <div className="activities-group">
                  <h4>Serviços Selecionados</h4>
                  <ul className="activities-list">
                    {selectedItem.selectedServicos.map((servico, index) => (
                      <li key={index} className={servico.completed ? 'completed' : ''}>
                        <span className="activity-check">
                          {servico.completed ? '✓' : '○'}
                        </span>
                        <span className="activity-name">{servico.nome}</span>
                        <span className="activity-value">{formatCurrency(servico.valor)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedItem.customActivities?.length > 0 && (
                <div className="activities-group">
                  <h4>Atividades Personalizadas</h4>
                  <ul className="activities-list">
                    {selectedItem.customActivities.map((activity, index) => (
                      <li key={index} className={activity.completed ? 'completed' : ''}>
                        <span className="activity-check">
                          {activity.completed ? '✓' : '○'}
                        </span>
                        <span className="activity-name">{activity.nome}</span>
                        <span className="activity-value">{formatCurrency(activity.valor)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedItem.reembolsos?.length > 0 && (
                <div className="activities-group">
                  <h4>Reembolsos</h4>
                  <ul className="activities-list">
                    {selectedItem.reembolsos.map((reembolso, index) => (
                      <li key={index} className={reembolso.completed ? 'completed' : ''}>
                        <span className="activity-check">
                          {reembolso.completed ? '✓' : '○'}
                        </span>
                        <span className="activity-name">{reembolso.descricao}</span>
                        <span className="activity-value">{formatCurrency(reembolso.valor)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {selectedItem.updatedAt && (
              <div className="modal-footer">
                <small className="last-updated">
                  Última atualização: {formatDate(selectedItem.updatedAt)} às{' '}
                  {new Date(selectedItem.updatedAt).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="planejador-cliente">
      <div className="planejador-header">
        <h1>Pedidos e Serviços</h1>
        <p>Acompanhe o progresso dos seus pedidos e serviços</p>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando pedidos e serviços...</p>
        </div>
      ) : (
        <>
          {summary && (
            <div className="summary-cards">
              <div className="summary-card">
                <div className="summary-icon">📊</div>
                <div className="summary-content">
                  <h3>Total de Pedidos</h3>
                  <span className="summary-value">{summary.total}</span>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon">💰</div>
                <div className="summary-content">
                  <h3>Valor Total Previsto</h3>
                  <span className="summary-value">{formatCurrency(summary.totalPrevisto)}</span>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon">✅</div>
                <div className="summary-content">
                  <h3>Valor Total Realizado</h3>
                  <span className="summary-value text-success">
                    {formatCurrency(summary.totalConsolidado)}
                  </span>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon">📈</div>
                <div className="summary-content">
                  <h3>Progresso Financeiro</h3>
                  <span className="summary-value">
                    {summary.totalPrevisto > 0 
                      ? Math.round((summary.totalConsolidado / summary.totalPrevisto) * 100) 
                      : 0}%
                  </span>
                </div>
              </div>
              
              {summary.pending > 0 && (
                <div className="summary-card warning">
                  <div className="summary-icon">⚠️</div>
                  <div className="summary-content">
                    <h3>Aguardando Autorização</h3>
                    <span className="summary-value">{summary.pending}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {(pendingApprovals.editRequests?.length > 0 || pendingApprovals.highValueApprovals?.length > 0) && (
            <div className="pending-approvals-section">
              <h2>Aprovações Pendentes</h2>
              
              {pendingApprovals.editRequests?.length > 0 && (
                <div className="approval-cards">
                  <h3>Solicitações de Edição</h3>
                  {pendingApprovals.editRequests.map(request => (
                    <div key={request._id} className="approval-card">
                      <div className="approval-header">
                        <h4>{request.workItemId.title}</h4>
                        <span className="approval-date">
                          Solicitado em {formatDate(request.requestedAt)}
                        </span>
                      </div>
                      <div className="approval-requester">
                        Por: {request.requestedBy.nomeCompleto}
                      </div>
                      <div className="approval-items">
                        <p>{request.items.length} itens - Total: {formatCurrency(request.totalValue)}</p>
                      </div>
                      <button
                        className="btn-process-approval"
                        onClick={() => handleOpenApprovalModal({ type: 'edit', data: request })}
                      >
                        Processar Solicitação
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {pendingApprovals.highValueApprovals?.length > 0 && (
                <div className="approval-cards">
                  <h3>Aprovações de Alto Valor</h3>
                  {pendingApprovals.highValueApprovals.map(approval => (
                    <div key={approval._id} className="approval-card high-value">
                      <div className="approval-header">
                        <h4>{approval.workItemId.title}</h4>
                        <span className="high-value-alert">Alto Valor!</span>
                      </div>
                      <div className="approval-item-details">
                        <p><strong>{approval.item.nome || approval.item.descricao}</strong></p>
                        <p className="item-value">{formatCurrency(approval.item.valor)}</p>
                      </div>
                      <div className="approval-requester">
                        Solicitado por: {approval.requestedBy.nomeCompleto}
                      </div>
                      <div className="approval-actions">
                        <button
                          className="btn-approve"
                          onClick={() => handleProcessHighValueApproval(approval._id, true)}
                        >
                          Aprovar
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => handleProcessHighValueApproval(approval._id, false)}
                        >
                          Rejeitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div className="planejador-filters">
            <div className="filter-group">
              <input
                type="text"
                className="filter-input"
                placeholder="Buscar por título ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                <option value="todo">A Fazer</option>
                <option value="inProgress">Em Progresso</option>
                <option value="done">Concluído</option>
              </select>
            </div>
            
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Visualização em Lista"
              >
                ☰
              </button>
              <button 
                className={`view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                onClick={() => setViewMode('kanban')}
                title="Visualização Kanban"
              >
                ⊞
              </button>
            </div>
            
            {lastRefresh && (
              <div className="last-refresh-indicator">
                <small>
                  Última atualização: {lastRefresh.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
                <button 
                  className="btn-refresh"
                  onClick={() => {
                    fetchWorkItems(true);
                    fetchPendingApprovals();
                  }}
                  title="Atualizar agora"
                >
                  ↻
                </button>
              </div>
            )}
          </div>
          
          {workItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>Nenhum pedido ou serviço encontrado</h3>
              <p>Você ainda não possui pedidos ou serviços cadastrados.</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="list-view">
              <table className="work-items-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Status</th>
                    <th>Autorização</th>
                    <th>Prioridade</th>
                    <th>Prazo</th>
                    <th>Responsável</th>
                    <th>Valor Previsto</th>
                    <th>Valor Realizado</th>
                    <th>Progresso</th>

                  </tr>
                </thead>
                <tbody>
                  {filteredWorkItems.map(item => {
                    const progress = calculateProgress(item);
                    const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'done';
                    
                    return (
                      <tr 
                        key={item._id} 
                        className={`${isOverdue ? 'overdue-row' : ''} ${!item.authorized ? 'unauthorized-row' : ''} clickable-row`}
                        onClick={() => handleCardClick(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="title-cell">
                          <div>{item.title}</div>
                          {item.description && (
                            <small className="description-preview">{item.description}</small>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge status-${item.status}`}>
                            {statusLabels[item.status]}
                          </span>
                        </td>
                        <td>
                          {item.authorized ? (
                            <span className="auth-status authorized">✓ Autorizado</span>
                          ) : (
                            <button 
                              className="btn-authorize-inline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAuthorize(item._id);
                              }}
                              disabled={authorizing}
                            >
                              Autorizar
                            </button>
                          )}
                        </td>
                        <td>
                          <span className={`priority-badge priority-${item.priority}`}>
                            {priorityLabels[item.priority]}
                          </span>
                        </td>
                        <td className={isOverdue ? 'text-danger' : ''}>
                          {formatDate(item.dueDate)}
                        </td>
                        <td>{item.assignedTo?.nomeCompleto || '-'}</td>
                        <td>{formatCurrency(item.totalValue)}</td>
                        <td className="text-success">{formatCurrency(item.totalConsolidado)}</td>
                        <td>
                          <div className="progress-cell">
                            <div className="progress-bar small">
                              <div 
                                className="progress-fill" 
                                style={{ 
                                  width: `${Math.min(progress, 100)}%`,
                                  backgroundColor: progress > 100 ? '#dc3545' : undefined
                                }}
                              />
                            </div>
                            <span className="progress-text">{progress}%</span>
                          </div>
                          <small className="activities-info">
                            {(() => {
                              const activities = calculateActivitiesProgress(item);
                              if (activities.total === 0) return 'Sem atividades';
                              return `${activities.completed}/${activities.total} atividades`;
                            })()}
                          </small>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredWorkItems.length === 0 && (
                <div className="empty-table">
                  <p>Nenhum pedido ou serviço encontrado com os filtros selecionados.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="kanban-board">
              <div className="kanban-column">
                <div className="column-header">
                  <h3>A Fazer</h3>
                  <span className="column-count">{groupedItems.todo.length}</span>
                </div>
                <div className="column-content">
                  {groupedItems.todo.map(item => (
                    <WorkItemCard key={item._id} item={item} />
                  ))}
                  {groupedItems.todo.length === 0 && (
                    <p className="empty-column">Nenhum pedido ou serviço a fazer</p>
                  )}
                </div>
              </div>
              
              <div className="kanban-column">
                <div className="column-header">
                  <h3>Em Progresso</h3>
                  <span className="column-count">{groupedItems.inProgress.length}</span>
                </div>
                <div className="column-content">
                  {groupedItems.inProgress.map(item => (
                    <WorkItemCard key={item._id} item={item} />
                  ))}
                  {groupedItems.inProgress.length === 0 && (
                    <p className="empty-column">Nenhum pedido ou serviço em progresso</p>
                  )}
                </div>
              </div>
              
              <div className="kanban-column">
                <div className="column-header">
                  <h3>Concluído</h3>
                  <span className="column-count">{groupedItems.done.length}</span>
                </div>
                <div className="column-content">
                  {groupedItems.done.map(item => (
                    <WorkItemCard key={item._id} item={item} />
                  ))}
                  {groupedItems.done.length === 0 && (
                    <p className="empty-column">Nenhum pedido ou serviço concluído</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {isModalOpen && <WorkItemModal />}
      {showApprovalModal && <EditRequestApprovalModal />}
    </div>
  );
};

export default PlanejadorCliente;
